import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { CreditCard, Loader2 } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useLanguage } from '@/i18n/LanguageContext';

export const CartSummary = ({ onCheckout, loading, siblingDiscount }) => {
  const {
    items, printItems, bundleItems,
    getBundleKey, getTotal, getDigitalTotal, getPrintTotal
  } = useCart();
  const { t } = useLanguage();

  const baseTotal = getTotal();
  const digitalTotal = getDigitalTotal();
  const printTotal = getPrintTotal();
  const discountAmt = siblingDiscount?.discount_amount || 0;
  const total = Math.max(0, baseTotal - discountAmt);

  return (
    <Card className="bg-card border-border sticky top-24">
      <CardContent className="p-6">
        <h3 className="text-lg font-bold mb-4">{t('cart.orderSummary')}</h3>

        <div className="space-y-3 mb-4">
          {items.length > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('prints.digitalItems')} ({items.length})</span>
              <span className="font-mono">CHF {digitalTotal.toFixed(2)}</span>
            </div>
          )}
          {bundleItems.length > 0 && bundleItems.map((bi) => {
            const key = getBundleKey(bi);
            return (
              <div key={key}>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground truncate mr-2">
                    {bi.name}{bi.student_name ? ` — ${bi.student_name}` : ''}
                  </span>
                  {bi.savings > 0 && (
                    <span className="font-mono line-through text-muted-foreground">CHF {bi.original_price.toFixed(2)}</span>
                  )}
                </div>
                {bi.savings > 0 && !bi.fixed_price && bi.discount_percent > 0 && (
                  <div className="flex justify-between text-sm text-green-500">
                    <span>{t('bundles.bundleDiscount')} ({bi.discount_percent}%)</span>
                    <span className="font-mono">-CHF {bi.savings.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-muted-foreground">{t('bundles.bundlePrice')}</span>
                  <span className="font-mono">CHF {bi.discounted_price.toFixed(2)}</span>
                </div>
              </div>
            );
          })}
          {printItems.length > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('prints.printItems')} ({printItems.length})</span>
              <span className="font-mono">CHF {printTotal.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('cart.tax')}</span>
            <span className="font-mono">CHF 0.00</span>
          </div>
          {siblingDiscount?.eligible && discountAmt > 0 && (
            <div className="flex justify-between text-sm text-green-500" data-testid="sibling-discount-line">
              <span>
                {t('cart.siblingDiscount').replace('{n}', siblingDiscount.distinct_students)} ({siblingDiscount.percent}%)
              </span>
              <span className="font-mono">-CHF {discountAmt.toFixed(2)}</span>
            </div>
          )}
        </div>

        <Separator className="my-4" />

        <div className="flex justify-between mb-6">
          <span className="font-bold">{t('cart.total')}</span>
          <span className="font-mono text-xl font-bold text-primary">CHF {total.toFixed(2)}</span>
        </div>

        <Button
          onClick={onCheckout}
          disabled={loading}
          className="w-full h-12 bg-primary hover:bg-primary/90 text-white uppercase tracking-widest font-bold"
          data-testid="checkout-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('cart.processing')}
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-2" />
              {t('cart.checkout')}
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center mt-4">
          {t('cart.securePayment')}
        </p>
      </CardContent>
    </Card>
  );
};
